import { useState } from 'react'
import { flushSync } from 'react-dom'
import { DateRangePicker, defaultStaticRanges, defaultInputRanges, createStaticRanges } from 'react-date-range'
import { addDays, endOfWeek, format, startOfToday, startOfWeek } from 'date-fns'
import { Dropdown, Spinner } from 'react-bootstrap'
import { Range, RangeKeyDict } from 'react-date-range'
import { enGB } from 'date-fns/locale'
import { useAppContext } from '../../../../AppContext'
import { savePref, loadPref, resolveLabel } from './date-range-preference'

export default function DateRangeSelector(opts: {
    onSubmit: (startDate: Date, endDate: Date) => Promise<void>
    pageKey?: string
    defaultLabel?: string
}) {
    const appContext = useAppContext()

    const getFromAndToDefault = () => {
        if(appContext.isPdf && appContext.fromDate && appContext.toDate) {
            return {fromDate: new Date(appContext.fromDate), toDate: new Date(appContext.toDate)}
        }

        if (opts.pageKey) {
            const saved = resolveLabel(loadPref(opts.pageKey))
            if (saved) return { fromDate: saved.startDate, toDate: saved.endDate }
        }

        if (opts.defaultLabel) {
            const defaultRange = resolveLabel(opts.defaultLabel)
            if (defaultRange) return { fromDate: defaultRange.startDate, toDate: defaultRange.endDate }
        }

        return {fromDate: addDays(new Date(), -30), toDate: new Date()}
    }
    const { fromDate, toDate } = getFromAndToDefault()
    const today = startOfToday()

    const [chosenRange, setChosenRange] = useState<Range[]>([{
        startDate: fromDate,
        endDate: toDate,
        key: 'selection'
    }])

    const [tempRange, setTempRange] = useState<Range[]>([{
        // startDate: addDays(new Date(), -30),
        // endDate: new Date(),
        startDate: fromDate,
        endDate: toDate,
        key: 'selection'
    }])

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [activePillLabel, setActivePillLabel] = useState<string | null>(null)
    const [savedDefault, setSavedDefault] = useState<string | null>(
        opts.pageKey ? (loadPref(opts.pageKey) ?? opts.defaultLabel ?? '30 Days') : null
    )

    const mondayWeekStart = { weekStartsOn: 1 as 1, locale: enGB }

    const remapStaticRange = (rangeObj: any) => {
        return {
            ...rangeObj,
            range: () => {
                const r = rangeObj.range()

                if (rangeObj.label.toLowerCase().includes('week')) {
                    const baseDate = rangeObj.label.toLowerCase().includes('last')
                        ? addDays(new Date(), -7)
                        : new Date()

                    return {
                        startDate: startOfWeek(baseDate, mondayWeekStart),
                        endDate: endOfWeek(baseDate, mondayWeekStart),
                        key: r.key || 'selection'
                    }
                }

                return r
            }
        }
    }

    const mondayStaticRanges = defaultStaticRanges.map(remapStaticRange)

    const customRanges = [
        { label: 'Last 7 Days', num: 7 },
        { label: 'Last 14 Days', num: 14 },
        { label: '30 Days', num: 30 },
        { label: '60 Days', num: 60 },
        { label: '90 Days', num: 90 },
    ].map(({ label, num }) => ({
        label,
        range: () => ({
            startDate: addDays(new Date(), -num),
            endDate: addDays(new Date(), -1),
            key: 'selection'
        })
    }))

    const allRangeDefinitions = [...mondayStaticRanges, ...customRanges]

    const customStaticRanges = createStaticRanges(
        allRangeDefinitions.map(r => ({ ...r, hasCustomRendering: true }))
    )


    const customInputRanges = defaultInputRanges.filter(
        (range: any) => !['days up to today', 'days starting today'].includes(range.label)
    )

    const handleSelect = (ranges: RangeKeyDict) => {
        setTempRange([ranges.selection])
        setActivePillLabel(null)
    }

    const isMobile = window.matchMedia('(max-width: 700px)').matches
    const orientation: 'horizontal' | 'vertical' = isMobile ? 'vertical' : 'horizontal'

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
    }

    const formatDateRange = (startDate?: Date, endDate?: Date): string => {
        if (!startDate || !endDate) return ''
        return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
    }

    const onSubmit = async () => {
        flushSync(() => {
            setIsLoading(true)
        })

        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve())
        })

        const startDate0 = tempRange[0].startDate
        const endDate0 = tempRange[0].endDate
        if(startDate0 && endDate0) {
            const startDate = new Date(startDate0)
            startDate.setHours(0,0,0,0)
            const endDate = new Date(endDate0)
            endDate.setHours(0,0,0,0)

            await opts.onSubmit(startDate, endDate)
            setChosenRange(tempRange)            
        }
        

        setIsLoading(false) 
        setIsOpen(false) 
    }

    return (
        <Dropdown show={isOpen} onToggle={toggleDropdown}>
            <Dropdown.Toggle 
                className="btn-sm btn-light dropdown-as-text"
            >
                {formatDateRange(chosenRange[0].startDate, chosenRange[0].endDate)}
            </Dropdown.Toggle>

            <Dropdown.Menu show={isOpen} align="end" className="p-3" style={{ minWidth: isMobile ? '320px' : '350px' }}>
                {isMobile && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '10px' }}>
                        {allRangeDefinitions.map((rangeObj: any) => {
                            const label: string = rangeObj.label
                            const isActive = label === activePillLabel
                            const isDefault = label === savedDefault
                            return (
                                <div
                                    key={label}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '4px 10px',
                                        borderRadius: '999px',
                                        border: '1px solid',
                                        borderColor: isActive ? '#0d6efd' : '#dee2e6',
                                        background: isActive ? '#0d6efd' : '#fff',
                                        color: isActive ? '#fff' : '#212529',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        fontSize: '12px',
                                    }}
                                    onClick={() => {
                                        const r = rangeObj.range()
                                        setTempRange([{ ...r, key: 'selection' }])
                                        setActivePillLabel(label)
                                    }}
                                >
                                    <span>{label}</span>
                                    {opts.pageKey && (
                                        <span
                                            role="button"
                                            title={isDefault ? 'Saved as default' : 'Save as default'}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (opts.pageKey) {
                                                    savePref(opts.pageKey, label)
                                                    setSavedDefault(label)
                                                    const r = rangeObj.range()
                                                    setTempRange([{ ...r, key: 'selection' }])
                                                    setActivePillLabel(label)
                                                }
                                            }}
                                            style={{
                                                color: isDefault ? '#f5a623' : (isActive ? 'rgba(255,255,255,0.6)' : '#bbb'),
                                                fontSize: '11px',
                                                cursor: 'pointer',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {isDefault ? '★' : '☆'}
                                        </span>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
                <DateRangePicker
                    onChange={handleSelect}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    ranges={tempRange}
                    staticRanges={isMobile ? [] : customStaticRanges}
                    inputRanges={isMobile ? [] : customInputRanges}
                    direction={orientation}
                    maxDate={today}
                    locale={enGB}
                    renderStaticRangeLabel={opts.pageKey ? (rangeObj: any) => {
                        const label: string = rangeObj.label
                        const isDefault = label === savedDefault
                        return (
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <span>{label}</span>
                                <span
                                    role="button"
                                    title={isDefault ? 'Saved as default' : 'Save as default'}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (opts.pageKey) {
                                            savePref(opts.pageKey, label)
                                            setSavedDefault(label)
                                            const resolved = resolveLabel(label)
                                            if (resolved) {
                                                setTempRange([{ startDate: resolved.startDate, endDate: resolved.endDate, key: 'selection' }])
                                            }
                                        }
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: '0 0 0 6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        lineHeight: 1,
                                        color: isDefault ? '#f5a623' : '#bbb',
                                        flexShrink: 0
                                    }}
                                >
                                    {isDefault ? '★' : '☆'}
                                </span>
                            </span>
                        )
                    } : undefined}
                />
                <div className="mt-3 d-flex justify-content-end">
                    <button 
                        className="btn btn-primary btn-sm" 
                        onClick={onSubmit} 
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Submit'} 
                    </button>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    )
}
